import React, { memo, useCallback } from 'react';
import { NodeProps, NodeResizer, Node } from '@xyflow/react';
import { ZoneData, NetworkZone } from '../types/diagram.types';
import { useDiagramStore } from '../store/diagramStore';

type ZoneNodeType = Node<ZoneData, 'zone'>;

const ZONE_STYLES: Record<NetworkZone, { bg: string; border: string; label: string }> = {
  'zone-external': { bg: 'rgba(158,158,158,0.06)', border: '#9E9E9E', label: 'External' },
  'zone-dmz':      { bg: 'rgba(255,109,0,0.06)',   border: '#FF6D00', label: 'DMZ' },
  'zone-kafka':    { bg: 'rgba(255,143,0,0.06)',   border: '#FF8F00', label: 'Kafka (Z5)' },
  'zone-internal': { bg: 'rgba(21,101,192,0.06)',  border: '#1565C0', label: 'Internal' },
  'zone-partners': { bg: 'rgba(106,27,154,0.06)',  border: '#6A1B9A', label: 'Partners' },
};

const ZoneNode = memo(({ data, selected, id }: NodeProps<ZoneNodeType>) => {
  const style = ZONE_STYLES[data.zoneId] ?? ZONE_STYLES['zone-internal'];
  const updateNodeSize = useDiagramStore(s => s.updateNodeSize);

  const onResizeEnd = useCallback(
    (_: unknown, params: { width: number; height: number }) => {
      updateNodeSize(id, params.width, params.height);
    },
    [id, updateNodeSize]
  );

  return (
    <>
      <NodeResizer
        minWidth={400}
        minHeight={120}
        isVisible={!!selected}
        lineStyle={{ borderColor: style.border }}
        handleStyle={{ borderColor: style.border, backgroundColor: '#fff' }}
        onResizeEnd={onResizeEnd}
      />
      <div
        style={{
          width: '100%',
          height: '100%',
          background: style.bg,
          border: `2px dashed ${style.border}`,
          borderRadius: 12,
          boxSizing: 'border-box',
          position: 'relative',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 8,
            left: 12,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: style.border,
            opacity: 0.85,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {data.label || style.label}
        </span>
      </div>
    </>
  );
});

ZoneNode.displayName = 'ZoneNode';
export default ZoneNode;
